import find from 'lodash.find';
import set from 'lodash.set';
import { createSelector } from 'reselect';
import { generateHashFromVars } from '../utils';
import {
  flattenAttributes,
  sortByResultScore,
} from '../utils/json-api';
import { distanceOptions } from '../data/distances';
import {
  companyType,
  levelOfExperiences,
} from '../enums/company';
import { transformFacetsToSelectFieldOption } from '../utils/search';
import { apiIndexFromState } from './api';
import { getFormFromStateSelector } from './form';
import {
  historyStateSelector,
  urlSearchSelector,
} from './router';

/**
 * Determine if the search view is loaded or not
 * @param {Object} state - redux store
 */
export const isSearchViewLoaded = ({ containerLoadState }) => containerLoadState?.Search?.loaded || false;

/**
 * Retrieve all employee professionnal profiles on the store (for search)
 * @param {Object} store
 * @moveto selectors/profile
 */
export const professionalEmployeesSelector = ({ api: { professionalEmployees } }) => professionalEmployees;

/**
 * Retrieve all mentors professionnal profiles on the store (for search)
 * @param {Object} store
 * @moveto selectors/profile
 */
export const professionalMentorsSelector = ({ api: { professionalMentors } }) => professionalMentors;

/**
 * Determine if current search is a recommendation display
 * @param {Object} state - redux store
 * @returns {Boolean|undefined} - return true if isRecommendations, but return undefined if not
 */
export const isRecommendationsSelector = createSelector(
  historyStateSelector,
  (routerState) => routerState?.isRecommendations,
);

/**
 * Retrieve hash of the current route
 * @param {Object} state - redux store
 */
export const searchUrlHashSelector = createSelector(
  isRecommendationsSelector,
  urlSearchSelector,
  (isRecommendations, searchParams) => {
    if (isRecommendations) {
      return 'recommendations';
    }
    return generateHashFromVars({
      ...searchParams,
      page: undefined,
      size: undefined,
    });
  }
);

/**
 * Determine if current search is loading
 * @param {Object} state - redux store
 */
export const isLoadingSearchIndexSelector = createSelector(
  searchUrlHashSelector,
  apiIndexFromState,
  (searchHash, apiIndexes) => apiIndexes?.[searchHash]?.loading || false,
);

/**
 * Retrieve current page loaded
 * @param {Object} state - redux store
 * @returns {Number}
 */
export const currentPageFromSearchIndexSelector = createSelector(
  searchUrlHashSelector,
  apiIndexFromState,
  (searchHash, apiIndexes) => apiIndexes?.[searchHash]?.page?.currentPage || 0,
);

/**
 * Retrieve total results pages
 * @param {Object} state - redux store
 */
export const numberOfPagesFromSearchIndexSelector = createSelector(
  searchUrlHashSelector,
  apiIndexFromState,
  (searchHash, apiIndexes) => apiIndexes?.[searchHash]?.page?.numberOfPages || Infinity,
);

/**
 * Retrieve search index
 * @param {Object} state - redux store
 */
export const getSearchResultsIndexFromState = createSelector(
  searchUrlHashSelector,
  apiIndexFromState,
  (searchHash, apiIndexes) => apiIndexes?.[searchHash]?.results || [],
);

export const doesSearchIndexExist = createSelector(
  searchUrlHashSelector,
  apiIndexFromState,
  (hash, index) => !!index?.[hash],
);

/**
 * Determine if the current page is the last
 * @param {Object} state - redux store
 */
export const isLastSearchPage = createSelector(
  currentPageFromSearchIndexSelector,
  numberOfPagesFromSearchIndexSelector,
  (currentPage, totalPages) => currentPage >= totalPages
);

/**
 * Retrieve last error occured on search page
 * @param {Object} state - redux store
 */
export const isErrorSearchPage = createSelector(
  searchUrlHashSelector,
  apiIndexFromState,
  (hash, index) => index?.[hash]?.error,
);

/**
 * Retrieve result with professional
 * @param {Object} state - redux store
 */
export const searchResultsSelector = createSelector(
  getSearchResultsIndexFromState,
  professionalEmployeesSelector,
  professionalMentorsSelector,
  (results, employees = [], mentors = []) => {
    const professionals = [
      ...employees.map(p => set(p, 'attributes.isEmployee', true)),
      ...mentors.map(p => set(p, 'attributes.isMentor', true)),
    ];
    return sortByResultScore(results
      .map(id => {
        const professional = find(professionals, { id });
        return flattenAttributes(professional);
      })
    );
  }
);

/**
 * Get current search results total count
 * @param {Object} state - redux store
 * @returns {String|Number} - number until 500 then string for 500+
 */
export const totalResultCountSelector = createSelector(
  searchUrlHashSelector,
  apiIndexFromState,
  (hash, index) => {
    const count = index?.[hash]?.page?.total || 0;
    if (count >= 500) {
      return '+500';
    }
    return count;
  }
);

// ==== FACETS SELECTORS ====

/**
 * Retrieve facets from search redux store entry
 * @param {Object} state - redux store
 */
export const facetsFromState = ({ search }) => search?.facets || {};

/**
 * Retrieve facet (tags)
 * Exclude not roots tags
 * @param {Object} state - redux store
 * @return {Object[]} [{key: '', docCount: 42}]
 */
export const facetTagsNameSelector = createSelector(
  facetsFromState,
  searchUrlHashSelector,
  (facets, hash) => {
    const tagsFacets = facets?.[hash]?.tagNames || {};
    const objectOfTagFacets = Object.values(tagsFacets);
    return transformFacetsToSelectFieldOption(objectOfTagFacets?.[0]?.attributes?.buckets || []);
  }
);

/**
 * Retrieve facet (sectors)
 * @param {Object} state - redux store
 * @return {Object[]} [{key: '', docCount: 42}]
 */
export const facetSectorSelector = createSelector(
  facetsFromState,
  searchUrlHashSelector,
  (facets, hash) => {
    const companySectorsFacet = facets?.[hash]?.sectors || {};
    const objectOfCompanySectorsFacet = Object.values(companySectorsFacet);
    return transformFacetsToSelectFieldOption(
      objectOfCompanySectorsFacet?.[0]?.attributes?.buckets || [],
    );
  }
);

/**
 * Retrieve facet (companies)
 * @param {Object} state - redux store
 * @return {Object[]} [{key: '', docCount: 42}]
 */
export const facetCompanySelector = createSelector(
  facetsFromState,
  searchUrlHashSelector,
  (facets, hash) => {
    const companiesFacet = facets?.[hash]?.companies || {};
    const objectOfCompaniesFacet = Object.values(companiesFacet);
    return transformFacetsToSelectFieldOption(
      objectOfCompaniesFacet?.[0]?.attributes?.buckets || [],
    );
  }
);

/**
 * Retrieve facet (company sizes)
 * @param {Object} state - redux store
 * @return {Object[]} [{key: '', docCount: 42}]
 */
export const facetCompanySizeSelector = createSelector(
  facetsFromState,
  searchUrlHashSelector,
  (facets, hash) => {
    const companySizesFacet = facets?.[hash]?.companySizes || {};
    const objectOfCompanySizeFacet = Object.values(companySizesFacet);
    const rawFacetValues = objectOfCompanySizeFacet?.[0]?.attributes?.buckets || [];
    const mergedValues = companyType.map(type => ({
      key: type.key,
      label: {
        fr: type.label,
      },
      docCount: type.key.split(',').reduce((prev, cur) => {
        const raw = rawFacetValues.find(r => r.key === cur);
        if (!raw) {
          return prev;
        }
        return prev + raw.docCount;
      }, 0),
    }));
    return transformFacetsToSelectFieldOption(mergedValues);
  }
);

/**
 * Retrieve facet (schools)
 * @param {Object} state - redux store
 * @return {Object[]} [{key: '', docCount: 42}]
 */
export const facetSchoolsSelector = createSelector(
  facetsFromState,
  searchUrlHashSelector,
  (facets, hash) => {
    const schoolsFacet = facets?.[hash]?.schoolNames || {};
    const objectOfSchoolsFacet = Object.values(schoolsFacet);
    return transformFacetsToSelectFieldOption(
      objectOfSchoolsFacet?.[0]?.attributes?.buckets || [],
    );
  }
);

/**
 * Retrieve facet (level of experience)
 * @param {Object} state - redux store
 * @return {Object[]} [{key: '', docCount: 42}]
 */
export const levelOfExperienceFacetSelector = createSelector(
  facetsFromState,
  searchUrlHashSelector,
  (facets, hash) => {
    const levelOfExperience = facets?.[hash]?.levelOfExperience || {};
    const objectOfLevelOfExperience = Object.values(levelOfExperience);
    const options = transformFacetsToSelectFieldOption(
      objectOfLevelOfExperience?.[0]?.attributes?.buckets || [],
    );
    return options.map(option => ({
      ...option,
      label: levelOfExperiences?.[option.label] || option.label,
    }));
  }
);

/**
 * Retrieve available options for distance
 * @param {Object} state - redux store
 */
export const distanceOptionsSelector = createSelector(
  facetsFromState,
  searchUrlHashSelector,
  urlSearchSelector,
  (facets, hash, params) => {
    if (params?.filters?.address_components?.locality) {
      const geoDistanceFacet = Object.values(facets?.[hash]?.geoDistance || {});
      const addressComponentFacet = Object.values(facets?.[hash]?.addressComponent || {});
      const facetValues = distanceOptions.map(({ label, value }) => {
        const distanceFacet = value === '50km' ? geoDistanceFacet : addressComponentFacet;
        return ({
          key: value,
          label: {
            fr: label,
          },
          value,
          docCount: distanceFacet?.[0]?.attributes?.buckets?.[0]?.docCount || 0,
        });
      });
      return transformFacetsToSelectFieldOption(facetValues);
    }
    return [];
  }
);

/**
 * Retrieve values from facets form
 * @param {Object} state - redux store
 */
export const getFacetsValues = createSelector(
  getFormFromStateSelector('facets'),
  (form) => form?.values,
);

/**
 * Retrieve registered fields from facets form
 * @param {Object} state - redux store
 */
export const getRegisteredFacetsFields = createSelector(
  getFormFromStateSelector('facets'),
  (form) => form?.registeredFields || {},
);
